import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '../../system/client/src/services/api';

/* Which "how to" hints this user has dismissed — fetched once per user per
 * page load and shared by every <Hint> on screen, so dismissing one updates
 * them all without refetching. Stored server-side (user_hints), so a
 * dismissed hint stays gone on every device. */

let loadedFor = null;       // user id the set below belongs to
let dismissed = null;       // Set of hint keys, or null while loading
let loading = null;         // in-flight promise
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn());

const load = (userId) => {
    if (loadedFor === userId && (dismissed || loading)) return;
    loadedFor = userId;
    dismissed = null;
    loading = api.get('hints')
        .then((data) => {
            if (loadedFor !== userId) return;
            dismissed = new Set(data?.dismissed || []);
        })
        .catch(() => {
            // Couldn't load: treat everything as dismissed rather than nag.
            if (loadedFor === userId) dismissed = new Set(['*']);
        })
        .finally(() => {
            loading = null;
            notify();
        });
};

const useHints = () => {
    const { user } = useAuth();
    const userId = user?.id || user?.userId || null;
    const [, force] = useState(0);

    useEffect(() => {
        const listener = () => force((n) => n + 1);
        listeners.add(listener);
        return () => listeners.delete(listener);
    }, []);

    useEffect(() => {
        if (userId) load(userId);
    }, [userId]);

    // Hold hints back while the first-run welcome tour is still to come.
    const tourPending = Boolean(user) && !user.onboarding_completed_at;

    const isOpen = (key) =>
        Boolean(userId) && !tourPending && loadedFor === userId && dismissed !== null
        && !dismissed.has('*') && !dismissed.has(key);

    const dismiss = (key) => {
        if (!dismissed || dismissed.has(key)) return;
        dismissed.add(key);
        notify();
        api.post(`hints/${key}/dismiss`).catch(() => {});
    };

    return { isOpen, dismiss };
};

export default useHints;
